import { useCallback, useRef, useState } from 'react'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import { useEventSubscription } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { useChatOperationScope } from './useChatOperationScope'

// Owned by the chat provider: closing a cart view must not abandon its mutations.
export function useChatCartMutationState(chatId: number | undefined, isLoggedIn: boolean, identityKey: string) {
  const captureCartOperationScope = useChatOperationScope(chatId, isLoggedIn, identityKey)
  const pending = useRef(new Set<symbol>())
  const [isFlightCartMutationPending, setPending] = useState(false)
  const reset = useCallback(() => {
    pending.current.clear()
    setPending(false)
  }, [])
  useEventSubscription('reset_chat_state', reset)
  useIsomorphicLayoutEffect(reset, [chatId, isLoggedIn, identityKey, reset])

  const beginFlightCartMutation = useCallback(() => {
    const ownsScope = captureCartOperationScope()
    const operation = Symbol('flight cart mutation')
    pending.current.add(operation)
    setPending(true)
    return () => {
      if (!ownsScope() || !pending.current.delete(operation)) return
      setPending(pending.current.size > 0)
    }
  }, [captureCartOperationScope])

  return { isFlightCartMutationPending, beginFlightCartMutation, captureCartOperationScope }
}
